import type { ChamberRow, ModuleSnapshot, TrayCell, VisionFeed } from '@ctcells/contract'
import type { ReactNode } from 'react'

import type { ValueRow } from '../../atoms/ValueRows.tsx'
import { FigurePanel } from '../../layout/FigurePanel.tsx'
import type { TabItem } from '../../layout/Tabs.tsx'
import { canView, type ScreenAccess } from '../access.ts'

import { FocusFigure, type OverviewParts } from './FocusFigure.tsx'
import { MonitoringPanel } from './MonitoringPanel.tsx'
import { findModule, type MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * 모니터링 · 패드 공통 뷰의 **본체** — README §14-3.
 *
 * ```
 * ┌ .fighead ─ (왼쪽 슬롯) ───────── [전체][트레이][원심분리기][비전][3축 로봇] ┐
 * ├───────────────────────────┬──────────────────────────────────────────────┤
 * │ 포커스 뷰 (제목 없음)      │ 사이드 패널 440px (제목 없음 · 제어 없음)     │
 * └───────────────────────────┴──────────────────────────────────────────────┘
 * ```
 *
 * 🔴 **콘솔 모니터링(`MonitoringScreen`)과 모듈 패드(`ModuleLocalScreen`)가 이것 하나를 쓴다.**
 *    두 화면이 다른 것은 **위에 모듈 카드 줄이 있느냐**와 **탭 목록**뿐이라 여기서 가르지 않는다.
 *    패드용 뷰를 따로 만들지 마라 — 인수인계가 명시적으로 금지한 항목이다.
 *
 * 🔴 탭 위치는 패널 오른쪽 위 고정이다(`FigurePanel` 이 쥔다). `leading` 에 무엇을 넣어도 안 움직인다.
 * 🔴 사이드 패널에 제어 버튼을 넣지 않는다. 조작은 개발 › 제어 또는 패드 「정비」 탭에서만 한다(§1-3).
 */
export interface ModuleFigureViewProps {
  deviceKey: MonitoringDeviceKey
  modules: readonly ModuleSnapshot[]
  chambers: readonly ChamberRow[]
  /**
   * 시료를 **못 봤을 때의 사유**. 넘어오면 챔버 표의 빈 문구가 「없음」이 아니라 이것이 된다 —
   * `GET /samples` 는 viewer 에게 403 이고(PII 접촉면), 그때 빈 표는 **거짓**이다.
   * 🔴 그림(`FocusFigure`)에는 넘기지 않는다 — 거기 빈 격자의 사유는 「칸 배정 정보 없음」이라
   *    **다른 사실**이고(BR-31), 둘을 한 문구로 합치면 어느 쪽인지 알 수 없게 된다.
   */
  chambersNotice?: string | undefined
  tray?: readonly TrayCell[] | undefined
  /**
   * 🔴 **없어도 「비전」 탭은 있다**(eric 지시 2026-09-21). 없을 때 비는 것은 탭 «안»이고,
   *    `FocusFigure` 가 「카메라 영상 연결 없음」을 적는다. 전날 호출부가
   *    `figureTabs(…, {vision:false})` 로 탭을 걷던 자리 — 되살리지 마라.
   */
  visionFeeds?: readonly VisionFeed[] | undefined
  access?: ScreenAccess
  /** 그림 탭. 구성은 `figureTabs(키)` 가 만든다 — 모듈마다 다르다(§19-3) */
  tabs: readonly TabItem[]
  component: string
  onChangeComponent: (id: string) => void
  /** 탭 줄 왼쪽 슬롯. 패드가 「응답 없음」 표시를 여기 건다 */
  leading?: ReactNode
  /**
   * 이 뷰가 모르는 탭(패드 「정비」)의 사이드 패널을 바깥에서 꽂는 자리.
   * 넘기면 기본 패널 대신 이것이 열린다.
   */
  sidePanel?: ReactNode
  /**
   * 포커스 뷰에 그릴 그림 키. 기본은 `component` 그대로다.
   * 「정비」처럼 그림이 따로 없는 탭에서 본체 그림을 유지하려고 갈라 둔다.
   */
  focusComponent?: string
  /** 사이드 패널 「정보」 구역에 덧붙일 행 — 모듈 로컬의 IP 주소·마지막 진단 */
  extraInfo?: readonly ValueRow[]
  /**
   * 「전체」 보기에 덧붙일 조각(레일 · 비전 아이콘 · 「작업 후」 트레이).
   * 🔴 **기본은 전부 꺼짐**이다 — 장비 관점 콘솔만 켠다. 그대로 `FocusFigure` 에 흘려보낸다.
   */
  overview?: OverviewParts | undefined
}

export function ModuleFigureView({
  deviceKey,
  modules,
  chambers,
  chambersNotice,
  tray,
  visionFeeds,
  access = {},
  tabs,
  component,
  onChangeComponent,
  leading,
  sidePanel,
  focusComponent,
  extraInfo,
  overview,
}: ModuleFigureViewProps) {
  const view = canView(access)
  const snapshot = findModule(modules, deviceKey)
  // 🔴 실행 여부는 **서버가 준 상태**다. 화면이 진행 중인지 판단하지 않는다
  const running = snapshot?.state === 'RUNNING'
  const figure = focusComponent ?? component

  // 「전체」는 그림만이다 — 사이드 패널을 열지 않는다(§14-3)
  const panel =
    sidePanel ??
    (component === 'all' ? undefined : (
      <MonitoringPanel
        canViewPii={view}
        chambers={chambers}
        chambersNotice={chambersNotice}
        component={component}
        deviceKey={deviceKey}
        onClose={() => onChangeComponent('all')}
        snapshot={snapshot}
        {...(extraInfo === undefined ? {} : { extraInfo })}
      />
    ))

  return (
    <FigurePanel
      onChange={onChangeComponent}
      tabs={tabs}
      value={component}
      {...(leading === undefined ? {} : { leading })}
      {...(panel === undefined ? {} : { sidePanel: panel })}
    >
      <FocusFigure
        canViewPii={view}
        chambers={chambers}
        component={figure}
        deviceKey={deviceKey}
        // 🔴 그림 클릭과 탭이 **같은 상태**를 본다. 상태를 두 벌 만들지 않는다
        onSelectComponent={onChangeComponent}
        running={running}
        selectableComponents={tabs.map((tab) => tab.id)}
        snapshot={snapshot}
        tray={tray}
        visionFeed={visionFeeds?.find((feed) => feed.module_id === deviceKey)}
        {...(overview === undefined ? {} : { overview })}
      />
    </FigurePanel>
  )
}
